import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import {
  SCOUT_POSITIONS,
  formatScoutPosition,
  SCOUT_PRIORITIES,
} from "../../constants/scoutRequestChoices";

// A chip that opens a small popover (design 1h filter row). Blue-tinted when a
// value is active. Closes on outside click / Escape.
const ChipPopover = ({ label, active, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition ${
          active
            ? "bg-[#E8F1FF] border-[#BFDBFE] text-[#1B6FE0]"
            : "bg-white border-[#E5E7EB] text-[#6B7280] hover:text-[#111827]"
        }`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute left-0 top-9 z-30 min-w-[180px] bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] py-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
};

const OptionRow = ({ selected, onClick, children }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#111827] hover:bg-gray-50 text-left"
  >
    <span className="flex-1 truncate">{children}</span>
    {selected && <Check className="w-4 h-4 text-[#1B6FE0]" />}
  </button>
);

const FEE_KINDS = [
  { value: "range", label: "Has a fee frame" },
  { value: "free_agents", label: "Free agents" },
  { value: "open", label: "Open (no frame)" },
];

const ScoutFilterBar = ({
  position, setPosition,
  country, setCountry, countryOptions,
  horizon, setHorizon,
  ageMin, setAgeMin, ageMax, setAgeMax,
  feeFrameKind, setFeeFrameKind,
  priority, setPriority,
  resultCount,
}) => {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Position */}
      <ChipPopover label={position ? formatScoutPosition(position) : "Position"} active={!!position}>
        {(close) => (
          <div className="max-h-64 overflow-y-auto">
            <OptionRow selected={!position} onClick={() => { setPosition(""); close(); }}>Any position</OptionRow>
            {SCOUT_POSITIONS.map((p) => (
              <OptionRow key={p.value} selected={position === p.value} onClick={() => { setPosition(p.value); close(); }}>
                {p.label}
              </OptionRow>
            ))}
          </div>
        )}
      </ChipPopover>

      {/* Country */}
      <ChipPopover label={country ? `Country: ${country}` : "Country"} active={!!country}>
        {(close) => (
          <div className="max-h-64 overflow-y-auto">
            <OptionRow selected={!country} onClick={() => { setCountry(""); close(); }}>Any country</OptionRow>
            {countryOptions.length === 0 && (
              <p className="px-3 py-1.5 text-[12.5px] text-[#9CA3AF]">No countries loaded yet.</p>
            )}
            {countryOptions.map((c) => (
              <OptionRow key={c} selected={country === c} onClick={() => { setCountry(c); close(); }}>{c}</OptionRow>
            ))}
          </div>
        )}
      </ChipPopover>

      {/* Age */}
      <ChipPopover
        label={ageMin || ageMax ? `Age ${ageMin || "0"}-${ageMax || "∞"}` : "Age"}
        active={ageMin !== "" || ageMax !== ""}
      >
        {(close) => (
          <div className="p-3 w-52">
            <div className="flex items-center gap-2">
              <input
                type="number" min="0" max="99" value={ageMin}
                onChange={(e) => setAgeMin(e.target.value)}
                placeholder="Min"
                className="w-full bg-[#F3F4F6] rounded-md px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
              />
              <span className="text-[#9CA3AF]">–</span>
              <input
                type="number" min="0" max="99" value={ageMax}
                onChange={(e) => setAgeMax(e.target.value)}
                placeholder="Max"
                className="w-full bg-[#F3F4F6] rounded-md px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
              />
            </div>
            <div className="flex justify-between mt-2">
              <button onClick={() => { setAgeMin(""); setAgeMax(""); }} className="text-[12px] text-[#6B7280] hover:text-[#111827]">Clear</button>
              <button onClick={close} className="text-[12px] font-semibold text-[#1B6FE0]">Apply</button>
            </div>
          </div>
        )}
      </ChipPopover>

      {/* Horizon */}
      <ChipPopover label={horizon ? `Horizon: ${horizon}` : "Horizon"} active={!!horizon}>
        {(close) => (
          <div className="p-3 w-60">
            <input
              value={horizon}
              onChange={(e) => setHorizon(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && close()}
              placeholder="e.g. Summer 2027, next 12 months"
              className="w-full bg-[#F3F4F6] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
            />
            <div className="flex justify-between mt-2">
              <button onClick={() => setHorizon("")} className="text-[12px] text-[#6B7280] hover:text-[#111827]">Clear</button>
              <button onClick={close} className="text-[12px] font-semibold text-[#1B6FE0]">Apply</button>
            </div>
          </div>
        )}
      </ChipPopover>

      {/* Fee frame */}
      <ChipPopover label="Fee frame" active={!!feeFrameKind}>
        {(close) => (
          <div>
            <OptionRow selected={!feeFrameKind} onClick={() => { setFeeFrameKind(""); close(); }}>Any</OptionRow>
            {FEE_KINDS.map((k) => (
              <OptionRow key={k.value} selected={feeFrameKind === k.value} onClick={() => { setFeeFrameKind(k.value); close(); }}>
                {k.label}
              </OptionRow>
            ))}
          </div>
        )}
      </ChipPopover>

      {/* Priority (agency-internal) */}
      <ChipPopover label={priority ? `Priority ${priority}` : "Priority"} active={!!priority}>
        {(close) => (
          <div>
            <OptionRow selected={!priority} onClick={() => { setPriority(""); close(); }}>Any priority</OptionRow>
            {SCOUT_PRIORITIES.map((p) => (
              <OptionRow key={p.value} selected={Number(priority) === p.value} onClick={() => { setPriority(String(p.value)); close(); }}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: p.dot }} />
                  {p.label} · {p.verb}
                </span>
              </OptionRow>
            ))}
          </div>
        )}
      </ChipPopover>

      <span className="text-[12.5px] text-[#6B7280] ml-1">
        {resultCount} request{resultCount === 1 ? "" : "s"}
      </span>
    </div>
  );
};

export default ScoutFilterBar;
